import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import SectionA from './components/SectionA';
import SectionB from './components/SectionB';
import Results from './components/Results';
import './App.css';

// Reset scroll on every route change so users don't have to scroll past
// a page header they've already seen on the previous step.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [pathname]);
  return null;
}

function YaeLogoMark({ className = 'navbar-logo' }) {
  // Inline SVG so the plum paths follow currentColor (theme-able from nav)
  return (
    <svg
      className={className}
      viewBox="0 0 1081 900"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-hidden="true"
    >
      <g transform="translate(0,900) scale(0.1,-0.1)" stroke="none">
        <g fill="currentColor">
          <path d="M5339 8917 l-47 -82 -476 -825 -476 -825 5 -14 5 -14 256 -443 256 -444 7 0 6 0 260 450 259 450 6 0 5 0 260 -450 259 -450 7 0 6 0 263 455 262 455 -76 133 -76 132 -448 775 -447 775 -15 2 -15 3 -46 -83z" />
          <path d="M3280 5336 l0 -4 794 -1374 793 -1373 -21 -40 -22 -40 -507 -878 -507 -878 0 -4 0 -5 531 2 530 3 1325 2294 1324 2294 0 4 0 3 -531 -2 -531 -3 -527 -912 -526 -913 -5 0 -5 0 -454 788 -455 787 -74 128 -74 127 -529 0 -529 0 0 -4z" />
          <path d="M1700 2613 l-1044 -1808 -19 -32 -19 -33 1064 0 1063 0 260 451 260 450 3 10 4 9 -527 0 -527 0 5 13 4 12 522 903 521 902 0 11 0 11 -252 437 -251 436 -12 18 -12 17 -1043 -1807z" />
          <path d="M6992 2582 l-1060 -1837 1065 -3 1065 -2 1058 2 1058 3 -263 455 -263 455 -1061 3 -1061 2 0 5 0 6 262 454 263 455 529 0 528 0 -6 16 -6 17 -512 886 -511 886 -12 17 -12 18 -1061 -1838z" />
        </g>
        <g fill="#E8542A">
          <path d="M5246 5583 l-149 -258 0 -10 0 -10 149 -257 149 -258 5 0 5 0 148 258 149 257 1 7 2 7 -150 261 -150 260 -5 0 -5 0 -149 -257z" />
        </g>
      </g>
    </svg>
  );
}

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav className="app-navbar">
      <div className="navbar-inner">
        <a
          href="https://yaeservices.biz"
          className="navbar-brand"
          aria-label="YAE Services — home"
        >
          <YaeLogoMark />
          <div className="navbar-divider" />
          <div className="navbar-title-group">
            <span className="navbar-title">YAE&nbsp;Services</span>
            <span className="navbar-subtitle">DPDP Risk Assessment</span>
          </div>
        </a>
        <button
          type="button"
          className="nav-toggle"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(o => !o)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 7h18M3 17h18" />
          </svg>
        </button>
        <div className={`nav-links${menuOpen ? ' open' : ''}`}>
          <a href="https://www.yaeservices.biz/build.html">Build</a>
          <a href="https://www.yaeservices.biz/grow.html">Grow</a>
          <a href="https://www.yaeservices.biz/run.html">Run</a>
          <a href="https://www.yaeservices.biz/automate.html">Automate</a>
          <a href="https://www.yaeservices.biz/blog.html">Blog</a>
          <a href="https://www.yaeservices.biz/about.html">About</a>
        </div>
        <a className="nav-cta" href="https://www.yaeservices.biz/contact.html#contact-form">
          Book a call →
        </a>
      </div>
    </nav>
  );
}

function HeroHeader() {
  return (
    <header className="app-header">
      <svg
        className="glyph-bg"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <g fill="currentColor">
          <path d="M50 8 L60 22 L50 28 L40 22 Z" />
          <path d="M48 32 L52 32 L54 38 L50 42 L46 38 Z" opacity="0.95" />
          <path d="M22 78 L40 50 L46 56 L36 78 Z" />
          <path d="M78 78 L60 50 L54 56 L64 78 Z" />
          <path d="M40 50 L60 50 L54 56 L46 56 Z" />
        </g>
      </svg>
      <div className="app-header-inner">
        <div className="eyebrow">
          <span>DPDP Compliance</span>
          <span className="dot">◆</span>
          <span>Risk Assessment</span>
        </div>
        <h1>
          DPDP <em>Readiness</em><br />
          &amp; Risk Assessment.
        </h1>
        <p className="subtitle">
          A practical compliance planner and risk quantifier for Indian SMBs preparing for
          the Digital Personal Data Protection Act, 2023.
        </p>
      </div>
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  const mailto = "mailto:info@yaeservices.biz?subject=Require%20more%20information&body=Hello%20Team%2C%0A%0AWould%20love%20to%20know%20more%20about%20the%20services%20you%20provide.%0A%0A%3CAdd%20more%20details%3E%0A%0AThanks%2C%0A%3CName%3E";
  const whatsapp = "https://wa.me/918484063453?text=We%20would%20like%20to%20know%20more";
  return (
    <footer className="app-footer">
      <div className="footer-inner">
        <div className="footer-grid">
          <div>
            <div className="footer-mark">
              YAE<em>.</em>
            </div>
            <p className="footer-tagline">
              One partnership. Four pillars. A technology partner that grows with you.
            </p>
          </div>
          <div className="footer-col">
            <h5>Services</h5>
            <ul>
              <li><a href="https://www.yaeservices.biz/build.html" target="_blank" rel="noopener noreferrer">Build</a></li>
              <li><a href="https://www.yaeservices.biz/grow.html" target="_blank" rel="noopener noreferrer">Grow</a></li>
              <li><a href="https://www.yaeservices.biz/run.html" target="_blank" rel="noopener noreferrer">Run</a></li>
              <li><a href="https://www.yaeservices.biz/automate.html" target="_blank" rel="noopener noreferrer">Automate</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h5>Company</h5>
            <ul>
              <li><a href="https://www.yaeservices.biz/about.html" target="_blank" rel="noopener noreferrer">About</a></li>
              <li><a href="https://www.yaeservices.biz/about.html#vision-mission" target="_blank" rel="noopener noreferrer">Vision &amp; culture</a></li>
              <li><a href="https://www.yaeservices.biz/blog.html" target="_blank" rel="noopener noreferrer">Blog</a></li>
              <li><a href="https://www.yaeservices.biz/contact.html#contact-form" target="_blank" rel="noopener noreferrer">Contact</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h5>Get in touch</h5>
            <ul>
              <li><a href={mailto}>info@yaeservices.biz</a></li>
              <li><a href={whatsapp} target="_blank" rel="noopener noreferrer">WhatsApp +91 84840 63453</a></li>
              <li><a href="https://www.yaeservices.biz/contact.html#contact-form" target="_blank" rel="noopener noreferrer">Book a 30-min call</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-tag">Technology as a catalyst, not a disruptor.</div>
        <div className="footer-bottom">
          <span>&copy; {year} YAE Services</span>
          <span>
            <a href="https://www.yaeservices.biz/build.html" target="_blank" rel="noopener noreferrer" className="footer-bottom-link">Build</a>
            <span className="sep">·</span>
            <a href="https://www.yaeservices.biz/grow.html" target="_blank" rel="noopener noreferrer" className="footer-bottom-link">Grow</a>
            <span className="sep">·</span>
            <a href="https://www.yaeservices.biz/run.html" target="_blank" rel="noopener noreferrer" className="footer-bottom-link">Run</a>
            <span className="sep">·</span>
            <a href="https://www.yaeservices.biz/automate.html" target="_blank" rel="noopener noreferrer" className="footer-bottom-link">Automate</a>
          </span>
          <span>
            <a href="https://www.yaeservices.biz/terms.html" target="_blank" rel="noopener noreferrer" className="footer-bottom-link">Terms</a>
            <span className="sep">·</span>
            <a href="https://www.yaeservices.biz/privacy.html" target="_blank" rel="noopener noreferrer" className="footer-bottom-link">Privacy</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

function Shell({ assessmentData, setAssessmentData }) {
  const { pathname } = useLocation();
  // Hero is a landing-page intro: only show it on the home route. On
  // /section-b and /results we want users to land directly on the content.
  const showHero = pathname === '/';

  return (
    <div className="App">
      <Navbar />
      {showHero && <HeroHeader />}
      <main className="app-main">
        <Routes>
          <Route
            path="/"
            element={
              <SectionA
                assessmentData={assessmentData}
                setAssessmentData={setAssessmentData}
              />
            }
          />
          <Route
            path="/section-b"
            element={
              assessmentData.showSectionB ? (
                <SectionB
                  assessmentData={assessmentData}
                  setAssessmentData={setAssessmentData}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/results"
            element={<Results assessmentData={assessmentData} />}
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  const [assessmentData, setAssessmentData] = useState({
    organizationName: '',
    sectionA: {},
    responses: {},
    showSectionB: false
  });

  return (
    <Router>
      <ScrollToTop />
      <Shell assessmentData={assessmentData} setAssessmentData={setAssessmentData} />
    </Router>
  );
}

export default App;
